<template>
  <div
    :class="classList"
    :style="cssVars"
    class="select-option"
    @click.capture="chooseOption"
  >
    <MProviderIcon
      v-if="showIcon && payload[iconKey]"
      :icon="payload[iconKey]"
      class="select-option__icon"
    />

    <div class="select-option__text-container">
      <div
        v-overflow-title="payload[textKey]"
        class="select-option__text-main"
      >
        <span class="select-option__text">
          {{ payloadText }}
        </span>

        <span
          v-if="largeWithIcon && labelText"
          class="select-option__label"
        >
          {{ labelText }}
        </span>
      </div>

      <div
        v-if="largeWithIcon && descriptionText"
        class="select-option__description"
      >
        {{ descriptionText }}
      </div>

      <div
        v-if="largeWithIcon && bottomBtnText"
        ref="bottomBtn"
        class="select-option__bottom-btn"
        @click.capture.stop="handleClickBottomBtn"
      >
        {{ bottomBtnText }}
      </div>
    </div>

    <div
      v-if="nonActive && selectIsOpen"
      class="m-help--tree-level"
      style="position: relative;"
    >
      <MHelp
        width="300px"
        class="select-option__help-icon"
        :popper-options="{modifiers: {
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport'
          }
        }}"
        :visible-arrow="false"
      >
        {{ warningSelectControl.text }}
      </MHelp>
    </div>
    <div
      v-if="hasWarning && selectIsOpen"
      class="m-help--tree-level"
      style="position: relative;"
    >
      <MHelp
        width="300px"
        class="select-option__help-icon"
        :popper-options="{modifiers: {
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport'
          }
        }}"
        :visible-arrow="false"
      >
        {{ warningText }}
      </MHelp>
    </div>
    <MProviderIcon
      v-for="(icon, idx) in payload.icons"
      :key="idx"
      :icon="icon"
      :position="payload.position"
      class="select-option__icon"
      :class="[payload.position === 'right' ? 'select-option__icon-after-right' : 'select-option__icon-after']"
    />
  </div>
</template>

<script>
  import createObserver from 'utils/observers/createObserver';
  import MHelp from 'components/Help/MHelp';
  import MProviderIcon from 'components/Design/MProviderIcon';
  import getTr from 'translation';

  export default {
    name: 'MSelectControlOption',
    components: {MProviderIcon, MHelp},
    props: {
      selectIsOpen: {
        type: Boolean,
        required: true
      },
      large: {
        type: Boolean,
        default: false
      },
      payload: {
        type: Object,
        default: Object
      },
      descriptionKey: {
        type: String,
        default: null
      },
      labelKey: {
        type: String,
        default: null
      },
      largeWithIcon: {
        type: Boolean,
        default: false
      },
      bottomBtnText: {
        type: String,
        default: null
      },
      textKey: {
        type: String,
        default: 'name'
      },
      isActive: Boolean,
      isOptgroup: Boolean,
      isStickable: {
        type: Boolean,
        default: false
      },
      iconKey: {
        type: String,
        default: 'icon'
      },
      showIcon: Boolean,
      stickyTriggerClass: {
        type: String,
        default: ''
      },
      indent: {
        type: Number,
        default: 0
      },
      rootElClass: {
        type: String,
        default: null
      },
      childIsSticky: {
        type: Boolean,
        default: false
      },
      warningSelectControl: {
        type: Object,
        default: () => ({
          text: getTr('you_cannot_select_this_item')
        })
      },
      idKey: {
        type: String,
        default: 'id'
      },
      showId: {
        type: Boolean,
        default: false
      },
      formatFn: {
        type: Function,
        default: undefined
      },
      stickyElemHeight: {
        type: Number,
        default: null
      },
      currentFocusItemId: {
        type: [Number, String],
        default: null
      }
    },
    data () {
      return {
        isSticky: false
      };
    },
    computed: {
      classList () {
        return {
          'select-option_selectable': !this.isOptgroup,
          'select-option_optgroup': this.isOptgroup,
          'select-option_sticky': this.isSticky && !this.childIsSticky,
          'select-option_active': this.isActive,
          'select-option_large': this.large,
          'select-option_non_active': this.nonActive,
          'select-option_focus': this.isFocus,
          'select-option_large-with-icon': this.largeWithIcon,
          [this.payload.customClass]: this.payload.customClass
        };
      },
      labelText () {
        return this.payload[this.labelKey] || null;
      },
      descriptionText () {
        return this.payload[this.descriptionKey] || null;
      },
      isFocus () {
        return this.currentFocusItemId === this.payload.id;
      },
      initialCssPadding () {
        if (this.large) {
          return 20;
        }
        return 8;
      },
      payloadText () {
        if (this.formatFn) {
          return this.formatFn(this.payload);
        }
        if (this.showId) {
          return `№${this.payload[this.idKey]} ${this.payload[this.textKey]}`;
        }
        return this.payload[this.textKey];
      },
      cssVars () {
        return {
          '--select-option-indent': `${this.indent + this.initialCssPadding}px`,
          '--select-option-padding-side-val': `${this.initialCssPadding}px`,
          ...this.stickyElemHeight && this.isSticky ? {height: `${this.stickyElemHeight}px`} : {}
        };
      },
      nonActive () {
        return this.payload.notAllowedForSelect && !this.isActive;
      },
      hasWarning () {
        return this.payload.hasWarning === true;
      },
      warningText () {
        return this.payload.warningText;
      }
    },
    watch: {
      isFocus () {
        if (this.isFocus) {
          this.scrollToCurrentEl();
        }
      }
    },
    mounted () {
      if (this.isStickable) {
        this.createStickableObserver();
      }
    },
    methods: {
      handleClickBottomBtn (e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.$emit('clickBottomBtn');
      },
      scrollToCurrentEl () {
        const parent = this.$el.closest('.scroll-container');
        const parentHasScroll = parent && parent.scrollHeight > parent.clientHeight;

        if (parentHasScroll) {
          this.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      },
      createStickableObserver () {
        const parent = this.$el.parentElement;
        const root = document.querySelector(`.${this.rootElClass}`).parentElement;

        const {observerDestructor} = createObserver(
          'IntersectionObserver',
          parent.querySelector(`.${this.stickyTriggerClass}`),
          this.triggerSticky,
          {root, rootMargin: '-8px 0px 0px 0px'}
        );

        this.$on('hook:beforeDestroy', () => {
          observerDestructor();
        });
      },
      chooseOption (e) {
        const bottomBtnEl = this.$refs?.bottomBtn;
        
        if (this.bottomBtnText && bottomBtnEl && bottomBtnEl.contains(e.target)) {
          return;
        }

        // do not choose option if click is on MHelp or bottomBtn
        if (e.target.classList.contains('m-help__icon') ||
          e.target.parentElement.classList.contains('m-help__icon') || 
          e.target.closest('.select-option__bottom-btn')) {
          return;
        }
        this.$emit('clickOption');
      },
      triggerSticky (entites) {
        const prevState = this.isSticky;
        this.isSticky = !entites[0].isIntersecting;
        if (prevState !== this.isSticky) {
          this.$emit('triggerSticky', {isSticky: this.isSticky, el: this.$el});
        }
      }
    }
  };
</script>

<style>
  :root {
    --hover-background-color: var(--action-background-color);
    --hover-color: var(--secondary-text-color);
    --active-item-background-color: var(--action-color);
    --active-item-text-color: #FFFFFF;
    --active-hover-background-color: var(--action-hover-color);
    --options-text-color: var(--secondary-text-color);
  }

  .select-option_selectable {
    &:hover,
    &.select-option_focus {
      color: var(--hover-color);
      background-color: var(--hover-background-color);
    }
  }

  .select-option_sticky {
    box-shadow: 0 1px 4px rgba(129, 129, 129, 0.25);
  }

  .select-option {
    color: var(--options-text-color);

    cursor: pointer;

    display: flex;

    flex-direction: row;
    align-items: center;

    box-sizing: border-box;
    min-height: 30px;

    padding: 3px var(--select-option-padding-side-val) 3px var(--select-option-indent);

    transition: height 500ms;
  }

  .select-option_large {
    min-height: 32px;
  }

  .select-option__text {
    line-height: 16px;
    text-overflow: ellipsis;

    white-space: unset;

    overflow: hidden;

    /* webkit-box not working in firefox, but in this case it is ok, it will not break layout */
    display: -webkit-box;  /* stylelint-disable-line value-no-vendor-prefix */

    /* stylelint-disable property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    /* stylelint-enable property-no-vendor-prefix */
  }

  .select-option_non_active {
    color: var(--gray-text-color);

    user-select: none;

    &:hover,
    &.select-option_focus {
      color: var(--gray-text-color);

      cursor: not-allowed;

      background-color: var(--gray-color);
    }
  }

  .select-option_active {
    color: var(--active-item-text-color);
    background-color: var(--active-item-background-color);

    &:hover,
    &.select-option_focus {
      color: var(--active-item-text-color);
      background-color: var(--active-hover-background-color);
    }
  }

  .select-option_optgroup {
    font-weight: var(--medium-font-weight);

    cursor: default;

    position: sticky;
    top: 0;

    background-color: var(--background-color);
  }

  .select-option__icon {
    flex-shrink: 0;

    width: 14px;
    height: 14px;
  }

  .select-option__icon-after,
  .select-option__icon {
    margin-right: 5px;
  }

  .select-option__help-icon {
    margin-left: 6px;
  }

  .select-option__icon-after-right {
    margin-left: auto;
    margin-right: 0;
  }

  .select-option .select-option__icon-after:first-of-type {
    margin-left: 4px;
  }

  .select-option_active .m-help__icon {
    color: var(--active-item-text-color);
  }

  .select-option_large-with-icon {
    --hover-background-color: #F9F9F9;
    --active-item-background-color: #EBF4FD;
    --active-hover-background-color: #EBF4FD;

    padding: 14px;

    & .select-option__icon {
      width: 32px;
      height: 32px;

      margin-right: 12px;
    }

    & .select-option__text {
      font-size: 14px;
      font-weight: 500;

      color: #242424;
      line-height: 16px;
    }

    & .select-option__text-main {
      display: flex;
      align-items: baseline;
    }

    & .select-option__description {
      font-size: 12px;
      font-weight: 400;

      color: #696969;
      line-height: 14px;

      margin-top: 4px;
    }

    & .select-option__bottom-btn {
      margin-top: 4px;
      font-family: var(--main-font-family);
      color: #5585C1;
      font-weight: 400;
      font-size: 12px;
      line-height: 100%;
      letter-spacing: 0;
      transition: opacity 0.2s ease;
      width: fit-content;

      &:hover {
        opacity: 0.7;
      }
    }

    & .select-option__label {
      font-size: 10px;
      font-weight: 500;

      line-height: 11px;

      margin-left: 4px;

      background: linear-gradient(235.1deg, #4965FB -3.88%, #FF3D67 99.79%);
      /* stylelint-disable property-no-vendor-prefix */
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      /* stylelint-enable property-no-vendor-prefix */
    }
  }
</style>
